import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Activity, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import {
  Badge,
  Card,
  CardHeader,
  CardTitle,
  EmptyState,
  MetricCard,
  PageHeader,
  SkeletonRow,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { formatDateTime, formatNumber, formatPercent } from '@/lib/utils/format';
import { countryFlag, countryLabel, operatorLabel } from '@/lib/utils/phone';
import { fetchOverviewKpis, fetchRecentVerifications } from './api';
import {
  TimespanPicker,
  presetRange,
  type Timespan,
  type TimespanRange,
} from './components/TimespanPicker';
import type { VerificationRecord, VerificationResult } from '@/types/verification';

const RESULT_TONE: Record<VerificationResult, 'success' | 'warning' | 'danger'> = {
  SUCCESS: 'success',
  PENDING: 'warning',
  FAILED:  'danger',
};

const KPI_LABELS: Record<Timespan, { checks: string; success: string; visitors: string }> = {
  DAILY:   { checks: "Verifications today",     success: 'Success rate (today)',   visitors: 'Matches confirmed today' },
  MONTHLY: { checks: 'Verifications this month', success: 'Success rate (month)',   visitors: 'Matches this month' },
  YEARLY:  { checks: 'Verifications this year',  success: 'Success rate (year)',    visitors: 'Matches this year' },
  CUSTOM:  { checks: 'Verifications (range)',     success: 'Success rate (range)',   visitors: 'Matches (range)' },
};

export function OverviewPage() {
  const [timespan, setTimespan] = useState<Timespan>('DAILY');
  const [range, setRange] = useState<TimespanRange>(() => presetRange('DAILY'));

  const kpisQuery = useQuery({
    queryKey: ['overview', 'kpis', range.from, range.to],
    queryFn: () => fetchOverviewKpis({ from: range.from, to: range.to }),
  });

  const recentQuery = useQuery({
    queryKey: ['overview', 'recent', 10],
    queryFn: () => fetchRecentVerifications(10),
  });

  const labels = useMemo(() => KPI_LABELS[timespan], [timespan]);
  const kpis = kpisQuery.data;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Overview"
        subtitle="Monitor verification activity, client usage, and operational health."
        actions={
          <TimespanPicker
            value={timespan}
            range={range}
            onChange={(next, nextRange) => {
              setTimespan(next);
              setRange(nextRange);
            }}
          />
        }
      />

      {/* KPI row */}
      {kpisQuery.isError ? (
        <ErrorBanner
          message={isApiError(kpisQuery.error) ? kpisQuery.error.firstErrorMessage : 'Could not load dashboard metrics.'}
          onRetry={() => kpisQuery.refetch()}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MetricCard label={labels.checks} value={kpis ? formatNumber(kpis.todayAuthChecks) : '—'} delta={kpis ? kpis.todayAuthChecksDelta * 100 : undefined} icon={Activity} loading={kpisQuery.isLoading} />
          <MetricCard label={labels.success} value={kpis ? formatPercent(kpis.authSuccessRate * 100) : '—'} delta={kpis ? kpis.authSuccessRateDelta * 100 : undefined} icon={CheckCircle2} tone="success" loading={kpisQuery.isLoading} />
          <MetricCard label={labels.visitors} value={kpis ? formatNumber(kpis.visitorsIdentified) : '—'} delta={kpis ? kpis.visitorsIdentifiedDelta * 100 : undefined} icon={CheckCircle2} tone="signal" loading={kpisQuery.isLoading} />
          <MetricCard label="Failed verifications" value={kpis ? formatNumber(kpis.todayAuthChecks - kpis.visitorsIdentified) : '—'} icon={XCircle} tone="danger" loading={kpisQuery.isLoading} />
        </div>
      )}

      {/* Credit balance banner */}
      {kpis && kpis.remainingCredits > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-primary-200 bg-gradient-to-r from-primary-50 to-primary-50/50 px-5 py-4 dark:border-primary-500/20 dark:from-primary-500/10 dark:to-transparent">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-500/20">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-primary-600 dark:text-primary-400" aria-hidden>
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-primary-800 dark:text-primary-200">
                {formatNumber(kpis.remainingCredits)} verification credits remaining
              </p>
              <p className="mt-0.5 text-xs text-primary-600/80 dark:text-primary-400">
                {kpis.balanceExpiryDate
                  ? `Expires ${new Date(kpis.balanceExpiryDate as unknown as string).toLocaleDateString()}`
                  : 'No expiry date on file'}
              </p>
            </div>
          </div>
          <Badge tone="primary">Platform balance</Badge>
        </div>
      )}

      {/* Recent verifications table */}
      <Card noPadding>
        <div className="flex items-center justify-between px-5 py-4">
          <CardHeader className="m-0">
            <CardTitle>Recent verification requests</CardTitle>
          </CardHeader>
          {recentQuery.isFetching && (
            <RefreshCw className="h-3.5 w-3.5 animate-spin text-fg-subtle" aria-hidden />
          )}
        </div>
        <RecentVerificationsTable
          isLoading={recentQuery.isLoading}
          isError={recentQuery.isError}
          rows={recentQuery.data ?? []}
          onRetry={() => recentQuery.refetch()}
        />
      </Card>
    </div>
  );
}

function RecentVerificationsTable({
  isLoading,
  isError,
  rows,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  rows: VerificationRecord[];
  onRetry: () => void;
}) {
  if (isLoading) return (
    <div>
      {[0, 1, 2, 3, 4].map((i) => <SkeletonRow key={i} cols={7} />)}
    </div>
  );
  if (isError) {
    return (
      <div className="px-5 py-8">
        <ErrorBanner message="Could not load recent requests." onRetry={onRetry} />
      </div>
    );
  }
  if (rows.length === 0) {
    return <EmptyState message="No verification activity yet. Requests will appear here once clients start verifying." />;
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Date &amp; time</TH>
          <TH>Service</TH>
          <TH>Mobile</TH>
          <TH>Country</TH>
          <TH>Operator</TH>
          <TH>Result</TH>
          <TH className="text-right">Latency</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TR key={row.id}>
            <TD className="text-fg-muted">{formatDateTime(row.timestamp)}</TD>
            <TD>
              <Badge tone="primary">
                {row.serviceType === 'VERIFY_N_PLUS' ? 'Verify N+' : 'Verify N'}
              </Badge>
            </TD>
            <TD className="font-mono text-xs text-fg-muted">{row.mobileNumber}</TD>
            <TD>
              <span className="inline-flex items-center gap-1.5 text-sm">
                <span aria-hidden>{countryFlag(row.countryCode)}</span>
                <span className="text-fg-muted">{countryLabel(row.countryCode)}</span>
              </span>
            </TD>
            <TD className="text-fg-muted">{operatorLabel(row.operator)}</TD>
            <TD>
              <Badge tone={RESULT_TONE[row.result]}>{row.result}</Badge>
            </TD>
            <TD className="text-right text-fg-muted">
              {row.latencyMs != null ? `${row.latencyMs}ms` : '—'}
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" aria-hidden />
      <div className="flex-1">
        <p className="text-rose-700 dark:text-rose-300">{message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1.5 text-xs font-medium text-rose-600 underline-offset-2 hover:underline dark:text-rose-400"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
